import { motion } from "framer-motion";
import { SPEC_TABLE, CERTIFICATIONS } from "@/data/catalog";
import { ShieldCheck, FileText, Sprout, Globe2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const HIGHLIGHTS = [
  { icon: FileText, label: "COA per shipment", note: "Certificate of Analysis" },
  { icon: Sprout, label: "Phytosanitary", note: "Plant-health certified" },
  { icon: Globe2, label: "Farm-to-shipment", note: "Full traceability" },
  { icon: ShieldCheck, label: "Compliance-ready", note: "APEDA · FSSAI · Spices Board" },
];

export default function Specs() {
  const { t } = useLang();
  return (
    <section
      id="specs"
      data-testid="section-specs"
      className="border-t border-[#3A4536]/15 bg-[#EAE5D9]/60"
    >
      <div className="mx-auto max-w-[1400px] px-6 py-24 md:px-10 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16 max-w-3xl"
        >
          <div className="mb-6 flex items-center gap-3">
            <span className="h-px w-10 bg-[#B85D43]" />
            <span className="overline text-[#B85D43]">{t("specs.overline")}</span>
          </div>
          <h2 className="font-serif-display text-5xl font-light leading-[0.95] text-[#1A2118] md:text-7xl">
            {t("specs.h1")}
            <br />
            {t("specs.h2")}
          </h2>
        </motion.div>

        {/* Certifications */}
        <div className="mb-16 grid grid-cols-2 gap-px overflow-hidden rounded-[2px] border border-[#3A4536]/15 bg-[#3A4536]/15 md:grid-cols-4">
          {CERTIFICATIONS.map((c, i) => (
            <motion.div
              key={c.code}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="bg-[#F4F1EB] p-6 md:p-8"
              data-testid={`cert-${c.code}`}
            >
              <div className="font-serif-display text-4xl font-light text-[#B85D43] md:text-5xl">
                {c.code}
              </div>
              <div className="mt-3 text-sm font-medium text-[#1A2118]">
                {c.label}
              </div>
              <div className="mt-1 text-xs leading-relaxed text-[#3A4536]">
                {c.note}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* Spec table */}
          <div className="lg:col-span-8">
            <div className="overflow-hidden rounded-[2px] border border-[#3A4536]/15">
              <div className="grid grid-cols-12 bg-[#1A2118] px-5 py-3">
                <span className="col-span-5 overline text-[#D4A373]">{t("specs.param")}</span>
                <span className="col-span-4 overline text-[#D4A373]">{t("specs.method")}</span>
                <span className="col-span-3 overline text-[#D4A373]">{t("specs.typical")}</span>
              </div>
              {SPEC_TABLE.map((row, i) => (
                <motion.div
                  key={row.param}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  className="grid grid-cols-12 items-center border-t border-[#3A4536]/10 bg-[#F4F1EB] px-5 py-4 transition-colors hover:bg-[#EAE5D9]"
                >
                  <span className="col-span-5 text-sm font-medium text-[#1A2118]">
                    {row.param}
                  </span>
                  <span className="col-span-4 text-sm text-[#3A4536]">
                    {row.method}
                  </span>
                  <span className="col-span-3 text-sm text-[#B85D43]">
                    {row.typical}
                  </span>
                </motion.div>
              ))}
            </div>
            <p className="mt-4 text-xs italic text-[#3A4536]">
              {t("specs.footnote")}
            </p>
          </div>

          {/* Highlights */}
          <div className="lg:col-span-4">
            <div className="grid grid-cols-1 gap-4">
              {HIGHLIGHTS.map((h, i) => (
                <motion.div
                  key={h.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="flex items-start gap-4 rounded-[2px] border border-[#3A4536]/15 bg-[#F4F1EB] p-5"
                >
                  <h.icon strokeWidth={1.5} className="mt-0.5 h-6 w-6 text-[#4A5D4E]" />
                  <div>
                    <div className="text-sm font-medium text-[#1A2118]">
                      {h.label}
                    </div>
                    <div className="text-xs text-[#3A4536]">{h.note}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
